import HundredBricksAPI from '@lib/hundredBricksAPI/HundredBricksAPI';
import { APIError } from '@lib/api';
import Pagination, { InvalidQueryParam } from '../../pagination';
import { SoldInvestment, SoldInvestments, SoldInvestmentsError } from './sold';
import DiversificationScore, { DiversificationScoreError } from './diversificationScore';
import Diversification, { DiversificationError } from './diversification';
import { InvalidFormatEntry, PortfolioInvestment, PortfolioInvestments, PortfolioInvestmentsError } from './portfolio';
import BricksDetail, { BricksInvestmentsNotFound, InvalidPropertyId, PropertyIsNotRegistered } from './portfolio/bricksDetail';
import OverviewReport, { CrowdfundingInvestment, ResaleInvestment, OverviewReportError } from './overviewReport';
import ProfitsSummary, { ProfitsSummaryError } from './profitsSummary';
import PropertyHealth, { PropertyHealthError } from './propertyHealth';
import AnnualSummary, { AnnualSummaryError } from './investmentSimulator/AnnualSummary';
import MonthlySummary, { MonthlySummaryError } from './investmentSimulator/MonthlySummary';
import Periods, { AnnualPeriodsError } from './investmentSimulator/AnnualPeriods';
import { CannotResendToken, ChannelIsNotSupported, InvestorDoesNotHaveALimitlessAccount, SendPurchaseToken, SendPurchaseTokenError, W8ContractNotSigned } from './SendPurchaseToken';
class InvestmentsAPI extends HundredBricksAPI {
    async getInvestments() {
        try {
            const { data } = await this.client.get('v1/investments');
            const crowdfundingInvestment = data.crowdfunding.map(property => new CrowdfundingInvestment(property));
            const resaleInvestment = data.resale.map(property => new ResaleInvestment(property));
            return new OverviewReport({
                ...data,
                crowdfunding: crowdfundingInvestment,
                resale: resaleInvestment
            });
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new OverviewReportError({ cause: error });
            }
            if (error instanceof Error) {
                throw new OverviewReportError('Unexpected error when obtaining investments', { cause: error });
            }
            throw new OverviewReportError(`Unexpected error when obtaining investments: '${error}'`);
        }
    }
    async getProfitsSummary(params) {
        try {
            const { data } = await this.client.get('v1/profits/summary', { params });
            return new ProfitsSummary(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new ProfitsSummaryError({ cause: error });
            }
            if (error instanceof Error) {
                throw new ProfitsSummaryError('Unexpected error when obtaining profit summary', { cause: error });
            }
            throw new ProfitsSummaryError(`Unexpected error when obtaining profit summary: '${error}'`);
        }
    }
    async getSoldInvestments(params) {
        try {
            const { data } = await this.client.get('v1/brickInvestments/sold', { params });
            const pagination = new Pagination(data.pagination);
            const investments = data?.items.map(investment => new SoldInvestment(investment));
            return new SoldInvestments({ investments, pagination, count: data.count });
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new InvalidQueryParam('page', params.page, { cause: error });
                    case 'PER_PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new InvalidQueryParam('perPage', params.perPage, { cause: error });
                    case 'INVALID_PAGE':
                        throw new InvalidQueryParam('page', params.page, { cause: error });
                    case 'INVALID_PER_PAGE':
                        throw new InvalidQueryParam('perPage', params.perPage, { cause: error });
                    default:
                        throw new SoldInvestmentsError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new SoldInvestmentsError('Unexpected error when obtaining sold investments', { cause: error });
            }
            else {
                throw new SoldInvestmentsError(`Unexpected error when obtaining sold investments: '${error}'`);
            }
        }
    }
    async getPortfolioInvestments(params) {
        try {
            const { data } = await this.client.get('v1/brickInvestments', { params });
            const pagination = new Pagination(data.pagination);
            const investments = data?.items.map(investment => new PortfolioInvestment(investment));
            return new PortfolioInvestments({ investments, pagination, count: data.count });
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'VALIDATION_ERROR':
                        throw new InvalidFormatEntry({ cause: error });
                    case 'PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new InvalidQueryParam('page', params.page, { cause: error });
                    case 'PER_PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new InvalidQueryParam('perPage', params.perPage, { cause: error });
                    case 'INVALID_PAGE':
                        throw new InvalidQueryParam('page', params.page, { cause: error });
                    case 'INVALID_PER_PAGE':
                        throw new InvalidQueryParam('perPage', params.perPage, { cause: error });
                    default:
                        throw new PortfolioInvestmentsError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new PortfolioInvestmentsError('Unexpected error when obtaining portfolio investments', { cause: error });
            }
            else {
                throw new PortfolioInvestmentsError(`Unexpected error when obtaining portfolio investments: '${error}'`);
            }
        }
    }
    async getPortfolioInvestmentsDetailByProperty(propertyId) {
        try {
            const { data } = await this.client.get(`v1/properties/${propertyId}/brickInvestments`);
            return new BricksDetail(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVALID_PROPERTY_ID':
                        throw new InvalidPropertyId(propertyId, { cause: error });
                    case 'PROPERTY_NOT_FOUND':
                        throw new PropertyIsNotRegistered(propertyId, { cause: error });
                    case 'BRICKS_INVESTMENTS_NOT_FOUND':
                        throw new BricksInvestmentsNotFound({ cause: error });
                    default:
                        throw new PortfolioInvestmentsError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new PortfolioInvestmentsError('Unexpected error when obtaining portfolio investment detail', { cause: error });
            }
            else {
                throw new PortfolioInvestmentsError(`Unexpected error when obtaining portfolio investment detail: '${error}'`);
            }
        }
    }
    async getDiversification() {
        try {
            const { data } = await this.client.get('v1/diversification');
            return new Diversification(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new DiversificationError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new DiversificationError('Unexpected error when obtaining diversification', { cause: error });
            }
            else {
                throw new DiversificationError(`Unexpected error when obtaining diversification: '${error}'`);
            }
        }
    }
    async getDiversificationScore() {
        try {
            const { data } = await this.client.get('v1/diversification/score');
            return new DiversificationScore(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new DiversificationScoreError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new DiversificationScoreError('Unexpected error when obtaining diversification score', { cause: error });
            }
            else {
                throw new DiversificationScoreError(`Unexpected error when obtaining diversification score: '${error}'`);
            }
        }
    }
    async getPropertyHealth() {
        try {
            const { data } = await this.client.get('v1/investments/health');
            return new PropertyHealth(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new PropertyHealthError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new PropertyHealthError('Unexpected error while obtaining property health', { cause: error });
            }
            else {
                throw new PropertyHealthError(`Unexpected error while obtaining property health: '${error}'`);
            }
        }
    }
    async getInvestmentSimulatorAnnualSummary(propertyId, params) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/investmentSimulation/annualSummary`, { params });
            return new AnnualSummary(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new AnnualSummaryError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new AnnualSummaryError('Unexpected error while obtaining investment simulator data', { cause: error });
            }
            else {
                throw new AnnualSummaryError(`Unexpected error while obtaining investment simulator data: '${error}'`);
            }
        }
    }
    async getInvestmentSimulatorMonthlySummary(propertyId, params) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/investmentSimulation/monthlySummary`, { params });
            return new MonthlySummary(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new MonthlySummaryError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new MonthlySummaryError('Unexpected error while obtaining investment simulator data', { cause: error });
            }
            else {
                throw new MonthlySummaryError(`Unexpected error while obtaining investment simulator data: '${error}'`);
            }
        }
    }
    async getInvestmentSimulatorAnnualPeriods(propertyId, params) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/investmentSimulation/annualPeriods`, { params });
            return new Periods(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new AnnualPeriodsError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new AnnualPeriodsError('Unexpected error while obtaining investment simulator data', { cause: error });
            }
            else {
                throw new AnnualPeriodsError(`Unexpected error while obtaining investment simulator data: '${error}'`);
            }
        }
    }
    async sendPurchaseToken(propertyId, channel) {
        try {
            const payload = {
                crowdfundingPropertyId: propertyId,
                channel
            };
            const { data } = await this.client.post('v1/crowdfundingInvestments/sendPurchaseToken', payload);
            return new SendPurchaseToken(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVESTOR_DOES_NOT_HAVE_A_LIMITLESS_ACCOUNT':
                        throw new InvestorDoesNotHaveALimitlessAccount({ cause: error });
                    case 'W8_CONTRACT_NOT_SIGNED':
                        throw new W8ContractNotSigned({ cause: error });
                    case 'CHANNEL_NOT_SUPPORTED':
                        throw new ChannelIsNotSupported(channel, { cause: error });
                    case 'CANNOT_RESEND_TOKEN':
                        throw new CannotResendToken({ cause: error });
                    default:
                        throw new SendPurchaseTokenError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new SendPurchaseTokenError('Unexpected error sending purchase confirmation token', { cause: error });
            }
            else {
                throw new SendPurchaseTokenError(`Unexpected error sending purchase code: '${error}'`);
            }
        }
    }
}
export default InvestmentsAPI;
